/**
 * Shared expression parser for manifest measures / series / panes
 * (MER-286). Supersedes per-component SIMPLE_EXPRESSION regexes in
 * MeasurePivotTable, ChartWidget, KpiComparisonTile.
 *
 * Two forms:
 *   1. SIMPLE   `agg(col) op agg(col)`                e.g. `sum(rev) - sum(cost)`
 *   2. COMPOUND `(agg(col) op agg(col)) op agg(col)`  e.g. `(sum(rev) - sum(cost)) / sum(rev)`
 *
 * The compound form was added for weighted-ratio measures like Margin %.
 * Without it, manifest authors fell back to `aggregation: sum` on a
 * `_pct` column, which sums fractions across rows and produces
 * impossible values (200%, 300%, 800% on the Revenue by Operation
 * table). The 3-term form folds left then applies the outer op.
 *
 * Aggregates supported: sum / avg / count / min / max.
 * Operators supported: + - * /. Division by zero yields null.
 */

const SIMPLE =
  /^\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*([+\-/*])\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*$/i;

const COMPOUND =
  /^\s*\(\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*([+\-/*])\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*\)\s*([+\-/*])\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*$/i;

export interface AggTerm {
  agg: string;
  column: string;
}

export interface SimpleAst {
  kind: "simple";
  left: AggTerm;
  op: string;
  right: AggTerm;
}

export interface CompoundAst {
  kind: "compound";
  left: AggTerm;
  op1: string;
  middle: AggTerm;
  op2: string;
  right: AggTerm;
}

export type ExpressionAst = SimpleAst | CompoundAst;

export function parseMeasureExpression(expression: string): ExpressionAst | null {
  const c = COMPOUND.exec(expression);
  if (c) {
    const [, agg1, col1, op1, agg2, col2, op2, agg3, col3] = c;
    return {
      kind: "compound",
      left: { agg: agg1.toLowerCase(), column: col1 },
      op1,
      middle: { agg: agg2.toLowerCase(), column: col2 },
      op2,
      right: { agg: agg3.toLowerCase(), column: col3 },
    };
  }
  const s = SIMPLE.exec(expression);
  if (s) {
    const [, agg1, col1, op, agg2, col2] = s;
    return {
      kind: "simple",
      left: { agg: agg1.toLowerCase(), column: col1 },
      op,
      right: { agg: agg2.toLowerCase(), column: col2 },
    };
  }
  return null;
}

export function applyOp(x: number, y: number, op: string): number | null {
  switch (op) {
    case "+":
      return x + y;
    case "-":
      return x - y;
    case "*":
      return x * y;
    case "/":
      return y === 0 ? null : x / y;
  }
  return null;
}

export function uniqueTerms(ast: ExpressionAst): AggTerm[] {
  if (ast.kind === "simple") return [ast.left, ast.right];
  return [ast.left, ast.middle, ast.right];
}

export function evaluateAst(
  ast: ExpressionAst,
  values: ReadonlyArray<number | null | undefined>,
): number | null {
  const [x, y, z] = values;
  if (x === null || x === undefined || y === null || y === undefined) return null;
  if (ast.kind === "simple") return applyOp(x, y, ast.op);
  if (z === null || z === undefined) return null;
  const inner = applyOp(x, y, ast.op1);
  if (inner === null) return null;
  return applyOp(inner, z, ast.op2);
}
